import React, { useState, useEffect, forwardRef } from 'react';
import BaseInput from '../BaseInput';
import isNaN from 'lodash/isNaN';
// Helpers
import { timeFormatter } from '../../../constants/Formatting';
import moment from 'moment';

const isValidDate = (date) => date instanceof Date && !isNaN(date);

/**
 * @typedef {Omit<React.ComponentPropsWithoutRef<BaseInput>, 'value'>} BaseInputProps
 *
 * @typedef {Object} AdditionalDateTimeInputProps
 * @property {string} [value] Represents the time value.
 *
 * @param {BaseInputProps & AdditionalDateTimeInputProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const CustomTimeInput = ({ onChange, value, onBlur, placeholder, ...props }, ref) => {
  const [textTime, setTextTime] = useState();

  const handleBlur = (e) => {
    if (e.target.value) {
      const formatted = timeFormatter(e.target.value);
      setTextTime(formatted);

      // Check if any change have happened to avoid firing onChange for the same value
      const newValue = moment(formatted, 'HH:mm');

      if (!value || !isValidDate(value) || newValue._d.toISOString() !== value.toISOString()) {
        onChange({ value: newValue._d });
      }
    } else {
      setTextTime('');

      // Check if Time value is not null already to avoid firing onChange for the same value
      if (value) {
        onChange({ value: null });
      }
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  useEffect(() => {
    if (value && !isValidDate(value)) {
      return;
    }

    if (value) {
      const text = moment(value);
      setTextTime(text.format('HH:mm'));
    } else {
      setTextTime('');
    }
  }, [value]);

  return (
    <BaseInput
      ref={ref}
      placeholder={placeholder || 'HH:mm or H'}
      {...props}
      className='k-input-inner'
      onBlur={handleBlur}
      value={textTime}
      onChange={(e) => setTextTime(e.target.value)}
    />
  );
};

/**
 * A custom time input component.
 *
 * @param {BaseInputProps & AdditionalDateTimeInputProps} props - Component props. Extends BaseInput props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Custom time input component.
 */
const ForwardCustomTimeInput = forwardRef(CustomTimeInput);

export default ForwardCustomTimeInput;
